import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

type ModifierKey = "altKey" | "ctrlKey" | "metaKey" | "shiftKey";
// Shortcuts that are used with modifier keys need to be mapped here
const CODE_TO_KEY_MAP: { [code: string]: string } = {
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  KeyT: "t",
};

// Checks if modifier key (alt, ctrl, meta, shift) is the ONLY modifier key pressed
function isModifierKeyPressed(event: KeyboardEvent, key: ModifierKey) {
  const otherKeys = ["altKey", "ctrlKey", "metaKey", "shiftKey"].filter(
    (k) => k !== key,
  ) as ModifierKey[];
  return event[key] && !otherKeys.some((k) => event[k]);
}

function isNoModifierKeyPressed(event: KeyboardEvent) {
  const keys: ModifierKey[] = ["altKey", "ctrlKey", "metaKey", "shiftKey"];
  return !keys.some((k) => event[k]);
}

// https://devtrium.com/posts/how-keyboard-shortcut
// NOTE: use of the ctrl and meta modifier keys can be implemented in the future if needed
const useKeyPress = (
  keys: string[],
  altKeys: string[],
  shiftKeys: string[],
  callback: (key: string) => void,
  altCallback: (key: string) => void,
  shiftCallback: (key: string) => void,
  node: HTMLElement | null = null,
) => {
  // Implement the callback ref pattern
  const callbackRef = useRef(callback);
  const altCallbackRef = useRef(altCallback);
  const shiftCallbackRef = useRef(shiftCallback);
  useLayoutEffect(() => {
    callbackRef.current = callback;
    altCallbackRef.current = altCallback;
    shiftCallbackRef.current = shiftCallback;
  });

  const _handleKeyDown = (
    evt: KeyboardEvent,
    _keys: string[],
    _callBackRef: typeof callbackRef,
  ) => {
    if (_keys.some((key) => evt.key === key) || CODE_TO_KEY_MAP[evt.code]) {
      const keyToUse = _keys.some((key) => evt.key === key) ? evt.key : CODE_TO_KEY_MAP[evt.code];
      _callBackRef.current(keyToUse);
    }
  };

  // Handle what happens on key press
  const handleKeyDown = useCallback(
    (event: Event) => {
      // Do not handle key press when a `textarea` has focus
      if (
        document.activeElement?.nodeName === "INPUT" ||
        document.activeElement?.nodeName === "TEXTAREA"
      ) {
        return;
      }
      // Check if the key is part of the ones we want
      const evt = event as KeyboardEvent;
      if (isNoModifierKeyPressed(evt)) {
        _handleKeyDown(evt, keys, callbackRef);
      } else if (isModifierKeyPressed(evt, "altKey")) {
        _handleKeyDown(evt, altKeys, altCallbackRef);
      } else if (isModifierKeyPressed(evt, "shiftKey")) {
        _handleKeyDown(evt, shiftKeys, shiftCallbackRef);
      }
    },
    [keys, altKeys, shiftKeys],
  );

  useEffect(() => {
    // Target is either the provided node or the document
    const targetNode = node ?? document;
    // Attach the event listener
    targetNode.addEventListener("keydown", handleKeyDown);

    // Remove the event listener
    return () => targetNode.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, node]);
};

export { useKeyPress };
